
import { BarChart3, TrendingUp, TrendingDown, Wallet, Receipt, Landmark } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import DashboardLayout from "../../components/DashboardLayout";
import { reportsApi } from "@/lib/api";
import { localToday, formatCurrencyShort } from "@/lib/data";
import {
  Card, ExportButtons, HeaderTools, Kpi, KpiGrid, NoMoneyAccess, Note, RangeControls, ReportBody, ReportHeader, TableScroll,
  money, monthRange, pct, useCanSeeMoney, useRange, useReport, type ExportSpec,
} from "../ReportKit";

interface Summary {
  period: { from: string; to: string };
  received: {
    total: number; gst: number; income: number; count: number;
    by_method: { method: string; total: number; gst: number; count: number }[];
  };
  paid_out: {
    total: number;
    vendor_payments: { total: number; count: number };
    expenses: { total: number; count: number };
    by_category: { id: number | null; name: string; total: number; count: number }[];
  };
  profit: { amount: number; margin: number; status: "profit" | "loss" };
}

export default function FinancialSummaryPage() {
  const canSeeMoney = useCanSeeMoney();
  const range = useRange({ from: monthRange(0).from, to: localToday() });
  const { from, to } = range.applied;

  const state = useReport<Summary>(
    async () => (await reportsApi.financialSummary({ from_date: from, to_date: to })).data,
    [from, to],
    canSeeMoney,
  );

  return (
    <DashboardLayout>
      <div className="space-y-5">
        <ReportHeader
          icon={BarChart3}
          iconClass="text-emerald-600"
          title="Financial Summary"
          subtitle="Money actually received and paid out between these dates"
        >
          {canSeeMoney && (
            <HeaderTools>
              <RangeControls range={range} />
              {state.status !== 403 && (
                <ExportButtons
                  build={() => state.data && buildExport(state.data, { from, to })}
                  disabled={state.loading || !state.data}
                />
              )}
            </HeaderTools>
          )}
        </ReportHeader>

        {!canSeeMoney ? <NoMoneyAccess /> : (
          <ReportBody state={state}>
            {(d) => <SummaryView d={d} />}
          </ReportBody>
        )}
      </div>
    </DashboardLayout>
  );
}

const NOTE =
  "Cash view: counts money on the day it was received or paid, including payments on events that were later cancelled. " +
  "GST inside client payments is worked out from each event’s GST rate and kept out of profit. " +
  "For revenue and profit per event (before GST), see the Events report.";

/** Vendor payments plus each expense category, leaving out anything with nothing paid. */
const paidOutBreakdown = (p: Summary["paid_out"]) => [
  { name: "Vendor payments", total: p.vendor_payments.total, count: p.vendor_payments.count },
  ...p.by_category.map((c) => ({ name: c.name, total: c.total, count: c.count })),
].filter((row) => row.total > 0);

const methodName = (m: string) => m.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

/** Excel / Print: the headline figures, where the money went, payment methods and the cash P&L. */
function buildExport(d: Summary, period: { from: string; to: string }): ExportSpec {
  const r = d.received;
  const p = d.paid_out;
  const profit = d.profit.amount;
  const isProfit = profit >= 0;
  const paidOut = paidOutBreakdown(p);
  const less = (n: number) => (n ? -n : 0);
  const sum = (list: { total: number; count: number }[], key: "total" | "count") => list.reduce((t, x) => t + (Number(x[key]) || 0), 0);

  return {
    title: "Financial Summary",
    fileName: "financial-summary",
    period,
    summary: [
      { label: "Received (incl. GST)", value: r.total, type: "money" },
      { label: "GST collected", value: r.gst, type: "money" },
      { label: "Paid out", value: p.total, type: "money" },
      { label: isProfit ? "Profit" : "Loss", value: Math.abs(profit), type: "money" },
      { label: "Margin (of money received excl. GST)", value: d.profit.margin, type: "percent" },
    ],
    tables: [
      {
        title: "Where the money went",
        columns: [
          { label: "Paid for" },
          { label: "Payments", type: "number" },
          { label: "Amount", type: "money" },
          { label: "Share of paid out", type: "percent" },
        ],
        rows: paidOut.map((row) => [row.name, row.count, row.total, p.total > 0 ? Math.round((row.total / p.total) * 1000) / 10 : 0]),
        totals: paidOut.length ? ["Total", sum(paidOut, "count"), sum(paidOut, "total"), null] : undefined,
      },
      {
        title: "Received by payment method",
        columns: [
          { label: "Payment method" },
          { label: "Payments", type: "number" },
          { label: "Amount", type: "money" },
        ],
        rows: r.by_method.map((m) => [methodName(m.method), m.count, m.total]),
        totals: r.by_method.length ? ["Total", sum(r.by_method, "count"), sum(r.by_method, "total")] : undefined,
      },
      {
        title: "Profit & loss (cash)",
        columns: [{ label: "Item" }, { label: "Amount", type: "money" }],
        rows: [
          ["Received from clients (incl. GST)", r.total],
          ["Less GST collected", less(r.gst)],
          ["Received (excl. GST)", r.income],
          ["Paid to vendors", less(p.vendor_payments.total)],
          ["Other expenses", less(p.expenses.total)],
        ],
        totals: [isProfit ? "Profit" : "Loss", profit],
      },
    ],
    notes: [NOTE],
  };
}

function SummaryView({ d }: { d: Summary }) {
  const profit = d.profit.amount;
  const isProfit = profit >= 0;
  const r = d.received;
  const p = d.paid_out;

  const chart = [
    { name: "Received (excl. GST)", amount: r.income, fill: "#10b981" },
    { name: "Paid out", amount: p.total, fill: "#f59e0b" },
    { name: isProfit ? "Profit" : "Loss", amount: Math.abs(profit), fill: isProfit ? "#3b82f6" : "#ef4444" },
  ];
  const paidOutRows = paidOutBreakdown(p);

  return (
    <>
      <KpiGrid>
        <Kpi
          label="Received" icon={Wallet} tone="emerald"
          value={money(r.total)}
          sub={`${r.count} payment${r.count === 1 ? "" : "s"} · incl. ${money(r.gst)} GST`}
        />
        <Kpi
          label="GST collected" icon={Landmark} tone="violet"
          value={money(r.gst)}
          sub="Owed to the government, not income"
        />
        <Kpi
          label="Paid out" icon={Receipt} tone="amber"
          value={money(p.total)}
          sub={`Vendors ${money(p.vendor_payments.total)} · Expenses ${money(p.expenses.total)}`}
        />
        <Kpi
          label={isProfit ? "Profit" : "Loss"} icon={isProfit ? TrendingUp : TrendingDown} tone={isProfit ? "blue" : "red"}
          value={money(Math.abs(profit))}
          valueClass={isProfit ? "text-blue-800" : "text-red-700"}
          sub={`${pct(d.profit.margin)} of money received (excl. GST)`}
        />
      </KpiGrid>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Received vs paid out">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={chart} barCategoryGap="35%">
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f4" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => formatCurrencyShort(Number(v))} width={56} />
              <Tooltip formatter={(v: any) => money(v)} />
              <Bar dataKey="amount" radius={[6, 6, 0, 0]}>
                {chart.map((c, i) => <Cell key={i} fill={c.fill} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card title="Where the money went">
          {paidOutRows.length === 0 ? (
            <p className="text-sm text-stone-400 py-10 text-center">Nothing paid out in this period</p>
          ) : (
            <ul className="space-y-2">
              {paidOutRows.map((row) => {
                const share = p.total > 0 ? (row.total / p.total) * 100 : 0;
                return (
                  <li key={row.name}>
                    <div className="flex items-center justify-between gap-3 text-sm">
                      <span className="text-stone-700 truncate">{row.name} <span className="text-xs text-stone-400">({row.count})</span></span>
                      <span className="font-semibold text-stone-800 tabular-nums whitespace-nowrap">{money(row.total)}</span>
                    </div>
                    <div className="h-1.5 bg-stone-100 rounded-full overflow-hidden mt-1">
                      <div className="h-full bg-amber-400 rounded-full" style={{ width: `${share}%` }} />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      </div>

      <Card title="Received by payment method">
        {r.by_method.length === 0 ? (
          <p className="text-sm text-stone-400">No payments received in this period</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {r.by_method.map((m) => (
              <div key={m.method} className="bg-stone-50 rounded-lg p-3 border border-stone-100 min-w-0">
                <p className="text-xs font-medium text-stone-500 truncate capitalize">{m.method.replace(/_/g, " ")}</p>
                <p className="text-base font-bold text-stone-800 mt-1 tabular-nums">{money(m.total)}</p>
                <p className="text-xs text-stone-400">{m.count} payment{m.count === 1 ? "" : "s"}</p>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card title="Profit & loss (cash)">
        <TableScroll minWidth={360}>
          <table className="w-full text-sm">
            <tbody className="divide-y divide-stone-100">
              <tr>
                <td className="py-2.5 text-stone-600">Received from clients (incl. GST)</td>
                <td className="py-2.5 text-right font-semibold text-emerald-700 tabular-nums">{money(r.total)}</td>
              </tr>
              <tr>
                <td className="py-2.5 pl-4 text-xs text-stone-600">Less GST collected</td>
                <td className="py-2.5 text-right text-stone-500 tabular-nums">– {money(r.gst)}</td>
              </tr>
              <tr className="bg-stone-50">
                <td className="py-2.5 font-semibold text-stone-800">Received (excl. GST)</td>
                <td className="py-2.5 text-right font-semibold text-stone-800 tabular-nums">{money(r.income)}</td>
              </tr>
              <tr>
                <td className="py-2.5 pl-4 text-xs text-stone-600">Paid to vendors</td>
                <td className="py-2.5 text-right text-stone-500 tabular-nums">– {money(p.vendor_payments.total)}</td>
              </tr>
              <tr>
                <td className="py-2.5 pl-4 text-xs text-stone-600">Other expenses</td>
                <td className="py-2.5 text-right text-stone-500 tabular-nums">– {money(p.expenses.total)}</td>
              </tr>
              <tr className={isProfit ? "bg-emerald-50" : "bg-red-50"}>
                <td className={`py-3 font-bold ${isProfit ? "text-emerald-800" : "text-red-800"}`}>{isProfit ? "Profit" : "Loss"}</td>
                <td className={`py-3 text-right font-bold tabular-nums ${isProfit ? "text-emerald-700" : "text-red-700"}`}>
                  {isProfit ? "" : "– "}{money(Math.abs(profit))}
                </td>
              </tr>
            </tbody>
          </table>
        </TableScroll>
      </Card>

      <Note>{NOTE}</Note>
    </>
  );
}
